import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/services/api";
import type {
  CustomFieldSchemaInput,
  ExtractedMetadata,
  ExtractMetadataRequest,
} from "@/hooks/useExtractMetadata";
import type { ProjectCustomField } from "@/services/projectCustomFieldsApi";

export type MetadataJobStatus = "Queued" | "Processing" | "Done" | "Error";

export interface MetadataJobInfo {
  status: MetadataJobStatus;
  queue_position?: number;
  result?: ExtractedMetadata;
  error?: string;
  stale?: boolean;
}

export interface FileDocumentEntry {
  filename: string;
  document_id: string;
}

interface EnqueueJobInput {
  document_id: string;
  filename: string;
  original_name?: string;
  download_url?: string;
  model?: string;
}

interface EnqueueResponse {
  enqueued: { document_id: string; filename: string }[];
  queue_size: number;
}

export interface ReExtractDocInput {
  document_id: string;
  filename?: string;
  model?: string;
  download_url?: string;
}

interface ReExtractResponse extends EnqueueResponse {
  skipped?: { document_id: string; reason: string }[];
}

export interface StaleSummary {
  stale_document_ids: string[];
  stale_count: number;
  total_with_jobs: number;
}

interface StatusResponse {
  statuses: Record<string, MetadataJobInfo>;
  all_done: boolean;
  queue_size: number;
}

interface PendingJobsResponse {
  jobs: Array<{
    document_id: string;
    filename: string;
    original_name: string | null;
    download_url: string | null;
    status: MetadataJobStatus;
    queue_position?: number;
  }>;
  queue_size: number;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120; // 10 minutes

export function useMetadataQueue() {
  // Status map keyed by document_id — mirrors PICO's documentStatuses pattern
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, MetadataJobInfo>>({});
  // Maps file array index → { filename, document_id }
  const [fileDocumentMap, setFileDocumentMap] = useState<FileDocumentEntry[]>([]);
  const [queueSize, setQueueSize] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const documentIdsRef = useRef<string[]>([]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollCountRef.current = 0;
    setIsPolling(false);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const pollOnce = useCallback(
    async (
      docIds: string[],
      onAllDone?: (statuses: Record<string, MetadataJobInfo>) => void,
    ) => {
      try {
        const params = docIds.join(",");
        const data = await api.get<StatusResponse>(
          `/api/metadata-extraction/status?document_ids=${encodeURIComponent(params)}`,
        );
        setDocumentStatuses(data.statuses);
        setQueueSize(data.queue_size);
        if (data.all_done) {
          stopPolling();
          onAllDone?.(data.statuses);
        }
      } catch (err) {
        console.error("Metadata queue poll error:", err);
      }
    },
    [stopPolling],
  );

  const startPolling = useCallback(
    (docIds: string[], onAllDone?: (statuses: Record<string, MetadataJobInfo>) => void) => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
      }
      documentIdsRef.current = docIds;
      pollCountRef.current = 0;
      setIsPolling(true);

      // Immediate first poll
      pollOnce(docIds, onAllDone);

      pollingIntervalRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLLS) {
          stopPolling();
          return;
        }
        await pollOnce(docIds, onAllDone);
      }, POLL_INTERVAL_MS);
    },
    [pollOnce, stopPolling],
  );

  const enqueue = useCallback(
    async (
      requests: ExtractMetadataRequest[],
      projectId?: string,
      customFields?: ProjectCustomField[],
    ): Promise<FileDocumentEntry[]> => {
      const jobs: EnqueueJobInput[] = requests.map((req) => ({
        document_id: req.document_id!,
        filename: req.filename!,
        original_name: req.original_name,
        download_url: req.download_url,
        model: req.model,
      }));

      const customFieldSchema: CustomFieldSchemaInput[] | undefined = customFields?.map(
        (cf) => ({
          name: cf.name,
          field_type: cf.field_type,
          description: cf.description,
          options: cf.options,
        }),
      );

      const response = await api.post<EnqueueResponse>("/api/metadata-extraction/enqueue", {
        project_id: projectId,
        jobs,
        custom_fields: customFieldSchema,
      });

      const mapping: FileDocumentEntry[] = response.enqueued.map((e) => ({
        filename: e.filename,
        document_id: e.document_id,
      }));

      setFileDocumentMap(mapping);
      setQueueSize(response.queue_size);

      // Initialise all as Queued immediately
      const initial: Record<string, MetadataJobInfo> = {};
      mapping.forEach((m) => {
        initial[m.document_id] = { status: "Queued" };
      });
      setDocumentStatuses(initial);

      return mapping;
    },
    [],
  );

  const fetchStatus = useCallback(async (docIds: string[]): Promise<void> => {
    if (docIds.length === 0) return;
    try {
      const params = docIds.join(",");
      const data = await api.get<StatusResponse>(
        `/api/metadata-extraction/status?document_ids=${encodeURIComponent(params)}`,
      );
      // The backend returns {status:"Error", error:"Job not found"} for
      // documents that have never been extracted. For a one-shot fetch
      // (e.g. on dialog open) that's "no prior extraction", not an error
      // to surface. Drop those entries so we don't clobber UI state.
      const meaningful: Record<string, MetadataJobInfo> = {};
      for (const [id, info] of Object.entries(data.statuses)) {
        if (info.status === "Error" && info.error === "Job not found") continue;
        meaningful[id] = info;
      }
      setDocumentStatuses((prev) => ({ ...prev, ...meaningful }));
      setQueueSize(data.queue_size);
    } catch (err) {
      console.error("Metadata queue one-shot status error:", err);
    }
  }, []);

  const rekeyJob = useCallback(
    async (projectId: string, oldDocumentId: string, newDocumentId: string): Promise<void> => {
      if (oldDocumentId === newDocumentId) return;
      try {
        await api.post("/api/metadata-extraction/jobs/rekey", {
          project_id: projectId,
          old_document_id: oldDocumentId,
          new_document_id: newDocumentId,
        });
      } catch (err) {
        // Re-keying is best-effort: the doc still saves and the user can
        // re-extract manually. Log so it surfaces during development.
        console.error("Metadata queue rekey failed:", err);
      }
    },
    [],
  );

  const fetchStaleSummary = useCallback(
    async (projectId: string): Promise<StaleSummary> => {
      return api.get<StaleSummary>(
        `/api/projects/${encodeURIComponent(projectId)}/metadata-extraction/stale-summary`,
      );
    },
    [],
  );

  const reExtract = useCallback(
    async (projectId: string, documents: ReExtractDocInput[]): Promise<ReExtractResponse> => {
      const response = await api.post<ReExtractResponse>(
        "/api/metadata-extraction/re-extract",
        { project_id: projectId, documents },
      );
      setQueueSize(response.queue_size);
      // Mark enqueued docs as Queued immediately for UI feedback
      if (response.enqueued.length > 0) {
        setDocumentStatuses((prev) => {
          const next = { ...prev };
          response.enqueued.forEach((e) => {
            next[e.document_id] = { status: "Queued" };
          });
          return next;
        });
      }
      return response;
    },
    [],
  );

  const fetchPendingJobs = useCallback(async (projectId: string): Promise<PendingJobsResponse> => {
    return api.get<PendingJobsResponse>(
      `/api/metadata-extraction/pending?project_id=${encodeURIComponent(projectId)}`,
    );
  }, []);

  const cancelPending = useCallback(async (projectId: string): Promise<number> => {
    const resp = await api.delete<{ cancelled: number }>(
      `/api/metadata-extraction/pending?project_id=${encodeURIComponent(projectId)}`,
    );
    return resp.cancelled;
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setDocumentStatuses({});
    setFileDocumentMap([]);
    setQueueSize(0);
  }, [stopPolling]);

  return {
    documentStatuses,
    fileDocumentMap,
    queueSize,
    isPolling,
    enqueue,
    reExtract,
    rekeyJob,
    fetchStatus,
    fetchStaleSummary,
    startPolling,
    stopPolling,
    fetchPendingJobs,
    cancelPending,
    reset,
  };
}
